import React, { useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Box,
  Divider,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import Logo from '../assets/logo/logo.png';
import { Container, Home, Users, Weight } from 'lucide-react';
import { Insights, ListAlt, Logout, Money } from '@mui/icons-material';

// ========== CONSTANTS ==========
const SIDEBAR_FULL = 260;
const SIDEBAR_MINI = 68;   // icon-only collapsed width

// ========== PREFIX RESOLVER ==========
const getPrefixByEmail = () => {
  const email = sessionStorage.getItem('email');
  if (email === 'Albina@booncoffee.com') return '/albina';
  if (email === 'odoo-admin@alfaroojfresh.com')      return '/alfaroojfresh';
  return '';
};

// ========== STYLES ==========
const SidebarContainer = styled(Box, {
  shouldForwardProp: (p) => p !== 'expanded',
})(({ expanded }) => ({
  width: expanded ? SIDEBAR_FULL : SIDEBAR_MINI,
  height: '100%',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  background: 'radial-gradient(circle at top left, #1e3a8a 0%, #0f172a 70%)',
  color: '#fff',
  overflowX: 'hidden',
  transition: 'width 0.25s ease',
}));

const SidebarItem = styled(ListItemButton, {
  shouldForwardProp: (p) => p !== 'active' && p !== 'expanded',
})(({ theme, active, expanded }) => ({
  borderRadius: 12,
  margin: theme.spacing(0.5, expanded ? 1.5 : 0.8),
  padding: theme.spacing(1.2, expanded ? 2 : 1.4),
  justifyContent: expanded ? 'flex-start' : 'center',
  background: active
    ? 'linear-gradient(90deg, rgba(59,130,246,0.55), rgba(99,102,241,0.55))'
    : 'rgba(255,255,255,0.05)',
  boxShadow: active ? '0 4px 14px rgba(0,0,0,0.35)' : 'none',
  transition: 'all 0.25s ease',
  cursor: 'pointer',
  minHeight: 44,
  '&:hover': {
    background: 'linear-gradient(90deg, rgba(59,130,246,0.4), rgba(99,102,241,0.4))',
    transform: expanded ? 'translateX(5px)' : 'scale(1.08)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
  },
}));

// ========== COMPONENT ==========
const Sidebar = ({ isOpen, onToggle }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const theme     = useTheme();
  const cacheRef  = useRef();

  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Desktop: isOpen controls full ↔ mini
  // Mobile:  always render full width (drawer handles show/hide)
  const expanded = !isDesktop ? true : isOpen;

  const prefix = getPrefixByEmail();

  const menuItems = [
    { icon: <Home size={18} />,                 text: 'Home',             path: `${prefix}/home` },
    { icon: <Users size={18} />,                text: 'Suppliers',        path: `${prefix}/suppliers` },
    { icon: <Insights sx={{ fontSize: 18 }} />, text: 'Purchase Orders',  path: `${prefix}/purchase-orders` },
    { icon: <ListAlt sx={{ fontSize: 18 }} />,  text: 'Goods',            path: `${prefix}/goods` },
    { icon: <Weight size={18} />,               text: 'Measurements',     path: `${prefix}/unit` },
    { icon: <Container size={18} />,            text: 'Inventory',        path: `${prefix}/inventory-summary` },
    { icon: <Money sx={{ fontSize: 18 }} />,    text: 'Business Summary', path: `${prefix}/business-summary` },
  ];

  const handleMenuClick = (path) => {
    navigate(path);
    if (!isDesktop) onToggle();
  };

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    cacheRef.current = {};
    navigate('/');
  };

  const drawerContent = (
    <SidebarContainer expanded={expanded ? 1 : 0}>

      {/* ── Header ── */}
      <Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent={expanded ? 'space-between' : 'center'}
          px={expanded ? 2 : 1}
          py={1.5}
          sx={{ minHeight: 56 }}
        >
          {/* Logo — hide text logo in mini mode to avoid overflow */}
          {expanded && (
            <img alt="logo" width={110} src={Logo} style={{ display: 'block' }} />
          )}

          {/* Toggle button */}
          <IconButton
            onClick={onToggle}
            size="small"
            sx={{
              color: '#fff',
              '&:hover': { background: 'rgba(255,255,255,0.12)' },
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mx: expanded ? 2 : 1 }} />

        {/* ── Menu items ── */}
        <List sx={{ pt: 1 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Tooltip
                key={item.text}
                title={expanded ? '' : item.text}
                placement="right"
                arrow
              >
                <SidebarItem
                  active={isActive ? 1 : 0}
                  expanded={expanded ? 1 : 0}
                  onClick={() => handleMenuClick(item.path)}
                >
                  <ListItemIcon
                    sx={{
                      color: '#fff',
                      minWidth: 'auto',
                      mr: expanded ? 1.5 : 0,
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>

                  {expanded && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 800 : 600,
                        fontSize: '0.82rem',
                        letterSpacing: '0.02em',
                        noWrap: true,
                      }}
                    />
                  )}
                </SidebarItem>
              </Tooltip>
            );
          })}

          {/* ── Logout ── */}
          <Tooltip title={expanded ? '' : 'Logout'} placement="right" arrow>
            <SidebarItem
              expanded={expanded ? 1 : 0}
              onClick={logout}
              sx={{ marginTop: '30px' }}
            >
              <ListItemIcon
                sx={{
                  color: '#ff8a80',
                  minWidth: 'auto',
                  mr: expanded ? 1.5 : 0,
                  justifyContent: 'center',
                }}
              >
                <Logout fontSize="small" />
              </ListItemIcon>
              {expanded && (
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{
                    fontSize: '0.82rem',
                    letterSpacing: '0.02em',
                    color: '#ff8a80',
                  }}
                />
              )}
            </SidebarItem>
          </Tooltip>
        </List>
      </Box>

      {/* ── Footer ── */}
      {expanded && (
        <Box p={2} textAlign="center">
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem' }}>
            © {new Date().getFullYear()} — Dimensions
          </Typography>
        </Box>
      )}
    </SidebarContainer>
  );

  // ── Desktop: permanent drawer — width animates between full and mini ──
  if (isDesktop) {
    return (
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: isOpen ? SIDEBAR_FULL : SIDEBAR_MINI,
          flexShrink: 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width: isOpen ? SIDEBAR_FULL : SIDEBAR_MINI,
            boxSizing: 'border-box',
            border: 'none',
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // ── Mobile: temporary overlay — fully hidden when closed ──
  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onToggle}
      variant="temporary"
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': {
          width: SIDEBAR_FULL,
          boxSizing: 'border-box',
          border: 'none',
          overflowX: 'hidden',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;